import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import axios from "axios";
import { config, discussionAPI, userData } from "../config";

// Web counterpart of the mobile DiscussionThread. A thread is keyed by
// (module, recordId). Loads via intType 4 (thread for record), replies via
// intType 2, opens a brand-new thread via intType 1. Message kinds:
// 0 message, 1 decline, 2 query. See kg-backend discussion engine.
const KIND_TAG = {
  1: { label: "Declined", color: "error" },
  2: { label: "Query", color: "warning" },
};

export default function DiscussionThread({
  module,
  recordId,
  recordLabel,
  counterpartyId,
}) {
  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(0);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const empId = userData ? userData.emp_id : 0;
  const scrollRef = useRef(null);

  const post = (data) => {
    config.method = "POST";
    config.url = discussionAPI;
    config.data = JSON.stringify(data);
    return axios(config);
  };

  const load = async () => {
    try {
      const res = await post({ intType: 4, module, recordId: String(recordId) });
      // intType 4 returns result as [{ thread, messages, participants }].
      const wrap =
        res.data && res.data.result && res.data.result[0]
          ? res.data.result[0]
          : null;
      setThreadId(wrap && wrap.thread ? wrap.thread.id : 0);
      setMessages(wrap && wrap.messages ? wrap.messages : []);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      if (threadId) {
        await post({ intType: 2, intThreadID: threadId, intFromEmpID: empId, txtBody: text, intKind: 0 });
      } else {
        await post({
          intType: 1,
          module,
          recordId: String(recordId),
          recordLabel,
          intFromEmpID: empId,
          intToEmpID: counterpartyId || 0,
          txtBody: text,
          intKind: 0,
        });
      }
      setBody("");
      await load();
    } catch (e) {
      console.log(e);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ border: "1px solid #e6ebf2", borderRadius: 2, overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          backgroundColor: "#f4f6fb",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 13 }}>Discussion</Typography>
        <IconButton size="small" onClick={load}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box ref={scrollRef} sx={{ maxHeight: 300, overflowY: "auto", p: 1.5 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : messages.length === 0 ? (
          <Typography sx={{ color: "text.secondary", fontSize: 13, textAlign: "center", py: 2 }}>
            No messages yet. Raise a query or start the conversation.
          </Typography>
        ) : (
          messages.map((m) => {
            const mine = Number(m.from_emp_id) === Number(empId);
            const tag = KIND_TAG[m.kind];
            return (
              <Box
                key={m.id}
                sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mb: 1 }}
              >
                <Box
                  sx={{
                    maxWidth: "75%",
                    backgroundColor: mine ? "#d7ebea" : "#eef2f6",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                  }}
                >
                  {!mine && (
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#17706e" }}>
                      {m.from_name || "User"}
                    </Typography>
                  )}
                  {tag && <Chip size="small" label={tag.label} color={tag.color} sx={{ mb: 0.5, height: 18 }} />}
                  <Typography sx={{ fontSize: 13.5, color: "#1e2a29" }}>{m.body}</Typography>
                  <Typography sx={{ fontSize: 10.5, color: "#8a9a99", mt: 0.3 }}>
                    {m.created_at ? moment(m.created_at).format("DD MMM, hh:mm A") : ""}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1, p: 1.5, borderTop: "1px solid #eef2f6" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Write a message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <IconButton color="primary" onClick={send} disabled={sending}>
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Box>
  );
}
